import { useState, useEffect } from 'react';
import { productsAPI, suppliersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Pencil, Trash2, Package, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const SECTORS = ['Steel', 'Aluminum', 'Cement', 'Glass', 'Plastics', 'Metals', 'Chemicals', 'Other'];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sector: 'Steel', hs_code: '', monthly_production: 0, raw_materials: [], supplier_id: '' });
  const [changeReason, setChangeReason] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchProducts = () => {
    productsAPI.getAll().then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    suppliersAPI.getAll().then(r => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
  }, []);

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchSector = sectorFilter === 'all' || p.sector === sectorFilter;
    return matchSearch && matchSector;
  });

  const handleSubmit = async () => {
    if (editing) {
      if (!changeReason.trim() || changeReason.trim().length < 5) {
        toast.error('Please provide a reason for this change (min 5 chars) — required for audit compliance.');
        return;
      }
      await productsAPI.update(editing.id, { ...form, change_reason: changeReason.trim() });
      toast.success('Product updated and logged to audit trail');
    } else {
      await productsAPI.create(form);
      toast.success('Product created');
    }
    setDialogOpen(false);
    setEditing(null);
    setChangeReason('');
    setForm({ name: '', sector: 'Steel', hs_code: '', monthly_production: 0, raw_materials: [] });
    fetchProducts();
  };

  const handleDelete = async (id) => {
    await productsAPI.delete(id);
    fetchProducts();
  };

  const openEdit = (product) => {
    setEditing(product);
    setChangeReason('');
    setForm({ name: product.name, sector: product.sector, hs_code: product.hs_code, monthly_production: product.monthly_production, raw_materials: product.raw_materials || [], supplier_id: product.supplier_id || '' });
    setDialogOpen(true);
  };

  return (
    <div data-testid="products-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title">Products</h1>
        <Button onClick={() => { setEditing(null); setForm({ name: '', sector: 'Steel', hs_code: '', monthly_production: 0, raw_materials: [], supplier_id: '' }); setDialogOpen(true); }}
          className="bg-primary hover:bg-primary/90" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="product-search-input" />
        </div>
        <Select value={sectorFilter} onValueChange={setSectorFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="sector-filter">
            <SelectValue placeholder="All Sectors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="cp-table-wrapper animate-fade-in">
        <Table className="min-w-[640px] text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Sector</TableHead>
              <TableHead className="hidden md:table-cell">HS Code</TableHead>
              <TableHead className="text-right">Monthly Prod.</TableHead>
              <TableHead className="text-right">PCF (tCO2/t)</TableHead>
              <TableHead className="hidden md:table-cell text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>{[...Array(6)].map((_, j) => <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-30" /> No products found
              </TableCell></TableRow>
            ) : filtered.map(p => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="font-medium">
                  {p.name}
                  {/* Mobile-only: show HS code + inline actions under the name so nothing is lost */}
                  <div className="md:hidden mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="font-mono">{p.hs_code}</span>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-mobile-${p.id}`} className="h-6 w-6">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} data-testid={`delete-product-mobile-${p.id}`} className="h-6 w-6 text-destructive hover:text-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell><span className="cp-badge-indigo">{p.sector}</span></TableCell>
                <TableCell className="hidden md:table-cell font-mono text-xs">{p.hs_code}</TableCell>
                <TableCell className="text-right font-mono">{p.monthly_production?.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{p.pcf}</TableCell>
                <TableCell className="hidden md:table-cell text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`} className="text-destructive hover:text-destructive">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update product details, optionally assign a supplier, and record a change reason for the audit trail.' : 'Create a new product to start tracking its carbon footprint.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
            <div><Label>Sector</Label>
              <Select value={form.sector} onValueChange={v => setForm({ ...form, sector: v })}>
                <SelectTrigger data-testid="product-sector-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>HS Code</Label><Input value={form.hs_code} onChange={e => setForm({ ...form, hs_code: e.target.value })} data-testid="product-hscode-input" /></div>
            <div><Label>Monthly Production (tons)</Label><Input type="number" value={form.monthly_production} onChange={e => setForm({ ...form, monthly_production: Number(e.target.value) })} data-testid="product-production-input" /></div>
            <div data-testid="product-supplier-picker">
              <Label>Assigned Supplier <span className="text-xs text-muted-foreground font-normal">(optional — locks the product to a specific supplier for data submissions)</span></Label>
              <Select value={form.supplier_id || 'none'} onValueChange={v => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="product-supplier-select"><SelectValue placeholder="No supplier assigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None (any supplier can submit) —</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id} data-testid={`product-supplier-option-${s.id}`}>
                      {s.name || s.company_name} {s.country ? `· ${s.country}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="pt-2 border-t border-border" data-testid="change-reason-section">
                <Label className="flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                  Reason for change <span className="text-destructive">*</span>
                </Label>
                <Input value={changeReason} onChange={(e) => setChangeReason(e.target.value)}
                       placeholder="e.g. Corrected monthly output based on April production report"
                       className="mt-1.5" data-testid="change-reason-input" required />
                <p className="text-xs text-muted-foreground mt-1">
                  Required for CBAM / TÜV audit trail. Logged to Change Management log.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="product-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90" data-testid="product-submit-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
